import { useMemo, useState } from "react";
import { Badge } from "flowbite-react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { formatToReal } from "../../../utils/utils";

const PendenciasFinanceiras = ({
  pendenciasFinanceiras = [],
  quantidadeTotal,
  dataMaiorPendencia,
  valorTotal,
  temDataDisponibilidade = true,
  temContrato = true,
}) => {
  const [expandido, setExpandido] = useState(false);

  const hasContrato =
    pendenciasFinanceiras.length > 0 &&
    pendenciasFinanceiras.every((p) =>
      Object.prototype.hasOwnProperty.call(p, "contrato")
    );

  const LIMITE = 12;
  const total = pendenciasFinanceiras.length;
  const restantes = Math.max(total - LIMITE, 0);

  const listaExibida = useMemo(
    () =>
      expandido
        ? pendenciasFinanceiras
        : pendenciasFinanceiras.slice(0, LIMITE),
    [expandido, pendenciasFinanceiras]
  );

  let classGrid = 6;

  if (temDataDisponibilidade) classGrid += 1;

  if (hasContrato || temContrato) classGrid += 1;

  return (
    <>
      {/* Resumo */}
      <div className="flex flex-wrap gap-3 mb-5">
        {quantidadeTotal && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-700 px-3 py-1.5 rounded-full text-sm font-medium">
            <span>Quantidade Total:</span>
            <span>{quantidadeTotal}</span>
          </div>
        )}

        {dataMaiorPendencia && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium">
            <span>Data maior:</span>
            <span>{dataMaiorPendencia}</span>
          </div>
        )}

        {valorTotal && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-700 px-3 py-1.5 rounded-full text-sm font-medium">
            <span>Valor total:</span>
            <span>{formatToReal(valorTotal)}</span>
          </div>
        )}
      </div>

      {/* Tabela + Fade quando colapsado */}
      <div className="relative rounded-2xl border border-gray-200 overflow-hidden">
        <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
          <div
            className={`grid gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider grid-cols-${classGrid}`}
          >
            <div>Data</div>
            {temDataDisponibilidade && <div>Disponib.</div>}

            <div className="col-span-2">Informante</div>
            <div>Valor</div>
            <div>Cidade</div>
            <div>Tipo</div>
            {(hasContrato || temContrato) && <div>Contrato</div>}
          </div>
        </div>

        {/* Área rolável quando colapsado, expandida quando aberto */}
        <div
          className={`transition-all duration-300 ease-out ${
            expandido ? "max-h-[9999px]" : "max-h-auto"
          } overflow-hidden`}
        >
          {listaExibida.map((pendencia, index) => (
            <div
              key={index}
              className={`grid gap-2 px-4 py-3 text-sm items-center grid-cols-${classGrid} ${
                index % 2 === 0 ? "bg-white" : "bg-gray-50"
              }`}
            >
              <div className="text-gray-700">{pendencia?.data || "-"}</div>
              {temDataDisponibilidade && (
                <div className="text-gray-700">
                  {pendencia?.dataDisponibilidade || "-"}
                </div>
              )}

              <div className="font-medium text-gray-900 col-span-2">
                {pendencia?.informante || "-"}
              </div>
              <div className="font-bold text-red-700">
                {formatToReal(pendencia?.valor)}
              </div>
              <div className="text-gray-700">{pendencia?.cidade || "-"}</div>
              <div className="text-gray-700">
                {pendencia?.tipoPendencia || "-"}
              </div>
              {(hasContrato || temContrato) && (
                <div className="text-gray-700">
                  {pendencia?.contrato || "-"}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Fade de “tem mais” quando colapsado */}
        {!expandido && total > LIMITE && (
          <div className="pointer-events-none absolute inset-x-0 bottom-[52px] h-16 bg-gradient-to-t from-white to-white/0" />
        )}

        {/* Footer elegante */}
        {total > LIMITE && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-t border-gray-200">
            <button
              type="button"
              onClick={() => setExpandido((v) => !v)}
              aria-expanded={expandido}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {expandido ? (
                <>
                  <HiChevronUp className="text-lg" />
                  Ver menos
                </>
              ) : (
                <>
                  <HiChevronDown className="text-lg" />
                  Ver mais
                </>
              )}
            </button>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Badge color="gray">
                Exibindo {Math.min(LIMITE, total)} de {total}
              </Badge>
              {!expandido && restantes > 0 && (
                <span className="text-gray-500">(+{restantes} restantes)</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Aviso quando colapsado */}
      {!expandido && total > LIMITE && (
        <div className="text-sm mt-3 text-gray-500 text-center">
          Exibindo as últimas {LIMITE} ocorrências.
        </div>
      )}
    </>
  );
};

export default PendenciasFinanceiras;
